import { useEffect, useState } from "react";
import { Clock, Check, Calendar, Search } from "lucide-react";
import { Dialog, TextField } from "@mui/material";
import MiscliniousInvoiceForm, {
  ViewPaymentInvoice,
} from "./SAP_Payment_Invoice";
import { authFetch } from "../../../api";
import Loader from "../../components/Loader/Loader";
import { pdf } from "@react-pdf/renderer";
import PaymentInvoice from "./SAP_Payment_Invoice";
import { useNavigate } from "react-router-dom";
import No_Payment_History from "./No_Payment_History.jsx";
import AnimatedSnackbar from "../../../STMPages/components/snackbars/AnimatedSnackbar.jsx";

export default function PaymentHistory() {
  const user_id = localStorage.getItem("userId");
  const [expanded, setExpanded] = useState(null);
  const [openInvoice, setOpenInvoice] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const navigate = useNavigate();

  const fetchPaymentHistory = async () => {
    try {
      setLoading(true);
      const response = await authFetch("sap-route/fetch-payment-history", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id }),
      });

      const responseData = await response.json();

      if (responseData.success && Array.isArray(responseData.data)) {
        const formattedData = responseData.data.map((item, index) => ({
          id: index + 1,
          package_id: item?.package_id,
          sub_service_id: item?.sub_service_id?.id,
          scholars_program_id: item?.program_id?.id,
          programName: item.program_id?.name || "N/A",
          subServiceName: item.sub_service_id?.name || "N/A",
          details: [
            { label: "Total Payment", value: item.package_amount || 0 },
            { label: "Paid Payment", value: item.total_amount_paid || 0 },
            { label: "Pending Payment", value: item.pending_amount || 0 },
            { label: "Installments", value: item.installments_payable || 0 },
          ],
          currency: item?.package_details?.currency,
          stages: item.payments.map((p) => ({
            name: `Installment ${p.installment_number || 1}`,
            status: p.payment_status,
            amount: p.amount_paid || p.amount_due || 0,
            date: p.payment_date
              ? new Date(p.payment_date).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : null,
            payment_mode: p.payment_mode,
            receipt_id: p.receipt_id,
          })),
        }));
        setPaymentData(formattedData);
        setFilteredData(formattedData);
      } else {
        setPaymentData([]);
        setFilteredData([]);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReceiptDetails = async (receipt_id) => {
    try {
      setLoading(true);
      const res = await authFetch("sap-route/fetch-receipt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id,
          receipt_id,
        }),
      });

      const data = await res.json();

      setSelectedReceipt(data.data);
      return data;
    } catch (err) {
      console.error("Fetch Receipt Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (receiptData) => {
    try {
      if (!receiptData) {
        throw new Error("Invalid or missing receipt data");
      }

      const blob = await pdf(
        <ViewPaymentInvoice receiptData={receiptData} />
      ).toBlob();

      if (!blob) {
        throw new Error("Failed to generate invoice file");
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${receiptData?.receipt_number || "invoice"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Invoice download failed:", err);
      showSnackbar(" Failed to download invoice. Please try again later.");
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    fetchPaymentHistory();
    // fetchReceiptDetails();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredData(paymentData);
    } else {
      const lowerQuery = searchQuery.toLowerCase();
      const filtered = paymentData.filter(
        (item) =>
          item.programName.toLowerCase().includes(lowerQuery) ||
          item.subServiceName.toLowerCase().includes(lowerQuery)
      );
      setFilteredData(filtered);
    }
  }, [searchQuery, paymentData]);

  return (
    <div className="bg-gray-50 min-h-screen ">
      <AnimatedSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
      <div className="flex items-center justify-between mb-4">
        <div className="font-inter text-2xl font-semibold text-[#572575]">
          Payment History
        </div>
        <div className="relative w-[20%]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search "
            value={searchQuery} //  bind input
            onChange={(e) => setSearchQuery(e.target.value)} //  update on typing
            className="w-full pl-10 pr-20 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#572575] focus:border-transparent"
          />
        </div>
      </div>

      {loading ? (
        <Loader text="Loading your payment history" />
      ) : filteredData.length === 0 ? (
        <No_Payment_History isSearchActive={searchQuery.trim() !== ""} />
      ) : (
        <>
          {filteredData.map((program, index) => (
            <div
              key={program.id}
              className="bg-white rounded-xl shadow-md mb-6 p-4 md:p-6 border border-gray-100"
            >
              <div className="flex justify-between items-center">
                <div className="font-inter text-lg font-semibold text-[#9D0385]">
                  {program.programName}
                </div>

                <button
                  onClick={() => setExpanded(expanded === index ? null : index)}
                  className="font-inter text-sm font-semibold text-[#572575] flex items-center gap-1 hover:text-[#3c0e62] transition-colors"
                >
                  Installments Details
                  <span>{expanded === index ? "▲" : "▼"}</span>
                </button>
              </div>
              <div className="font-inter text-md font-semibold text-[#572575]">
                {program.subServiceName}
              </div>

              {/* Summary Details */}
              <div className="flex flex-wrap gap-x-10 gap-y-2 text-[#1E1E1E] mt-3">
                {program.details.map((detail, i) => (
                  <div key={i} className="flex flex-col gap-1">
                    <span className="font-inter text-sm">{detail.label}</span>
                    <span className="font-inter text-sm font-semibold">
                      {detail.label.includes("Installments")
                        ? detail.value
                        : `INR ${detail.value.toLocaleString()}`}
                    </span>
                  </div>
                ))}
              </div>

              {/* Stages */}
              {expanded === index && program.stages.length > 0 && (
                <div className="border-t border-gray-200 pt-6 mt-4">
                  <div
                    className="
        relative 
        flex flex-wrap md:flex-nowrap 
        items-start justify-center 
        gap-6 sm:gap-8 md:gap-12
      "
                  >
                    {program.stages.map((stage, i) => {
                      const stageCount = program.stages.length;

                      // Connector line styling
                      const connectorStyle = {
                        width: stageCount <= 4 ? "120%" : "120%",
                        left: "50%",
                        transform: "translateX(0)",
                      };

                      return (
                        <div
                          key={i}
                          className="
              flex flex-col items-center 
              flex-1 relative min-w-[150px] 
              max-w-[220px] sm:max-w-[250px] 
              w-full
            "
                        >
                          {/* Connector line */}
                          {i !== stageCount - 1 && (
                            <div
                              className="
                  hidden md:block 
                  absolute top-5 h-[2px] 
                  bg-gray-300 z-0
                "
                              style={connectorStyle}
                            ></div>
                          )}

                          {/* Circle */}
                          <div
                            className={`z-10 rounded-full p-2 sm:p-3 text-white shadow-md
                ${
                  stage.status === "paid"
                    ? "bg-[linear-gradient(114.65deg,#00C95E_22.38%,#00632E_103.15%)]"
                    : stage.status === "due"
                    ? "bg-[linear-gradient(114.21deg,#FF800C_22.77%,#FFD042_97.06%)]"
                    : "bg-[linear-gradient(114.21deg,#CECECE_22.77%,#7D7C77_97.06%)]"
                }`}
                          >
                            {stage.status === "paid" ? (
                              <Check size={22} className="sm:w-6 sm:h-6" />
                            ) : (
                              <Clock size={22} className="sm:w-6 sm:h-6" />
                            )}
                          </div>

                          {/* Label */}
                          <p className="mt-2 text-[13px] sm:text-sm font-semibold text-gray-800 text-center leading-tight">
                            {stage.name}
                          </p>

                          {/* Stage Card */}
                          <div
                            className={`
                mt-3 w-full sm:w-[90%] 
                rounded-lg p-3 sm:p-4 
                text-xs sm:text-sm shadow-sm
                ${
                  stage.status === "paid"
                    ? "bg-[#E5FFF1] border border-[#42AF75]"
                    : stage.status === "due"
                    ? "bg-[#FFFBEE] border border-[#DACEA6]"
                    : "bg-[#F9FAFB] border border-[#E5E7EB]"
                }
              `}
                          >
                            {/* Paid */}
                            {stage.status === "paid" && (
                              <>
                                <div className="flex justify-between items-center mb-1">
                                  <p className="flex items-center gap-1 text-[#000000] font-medium text-[12px] sm:text-[13px]">
                                    <Calendar size={12} /> {stage.date}
                                  </p>
                                  <span className="bg-white text-[#166534] px-2 py-[2px] border border-[#42AF75] rounded-md text-[11px] font-semibold">
                                    Paid
                                  </span>
                                </div>
                                <div className="mt-2 border-t border-dashed border-[#A7F3D0] pt-2 flex justify-between items-center">
                                  <div>
                                    <p className="text-gray-600 font-medium">
                                      Amount
                                    </p>
                                    <p className="font-bold text-gray-900 text-[15px] sm:text-[16px]">
                                      INR {stage.amount.toLocaleString()}
                                    </p>
                                  </div>
                                  <button
                                    title="Download Receipt"
                                    onClick={async () => {
                                      const data = await fetchReceiptDetails(
                                        stage.receipt_id
                                      );
                                      if (data?.data)
                                        await handleDownloadInvoice(data.data);
                                    }}
                                    className="bg-[#5B1A8E] hover:bg-[#481070] text-white p-2 rounded-md transition-all"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      strokeWidth={2}
                                      stroke="currentColor"
                                      className="w-4 h-4"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              </>
                            )}

                            {/* Due */}
                            {stage.status === "due" && (
                              <>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="bg-white border border-[#DACEA6] text-[#92400E] px-2 py-[2px] rounded-md text-[11px] font-semibold">
                                    Due
                                  </span>
                                </div>
                                <div className="mt-2 border-t border-dashed border-[#FCE7B2] pt-2 flex justify-between items-center">
                                  <div>
                                    <p className="text-gray-600 font-medium">
                                      Amount
                                    </p>
                                    <p className="font-semibold text-gray-900 text-[15px] sm:text-[16px]">
                                      INR {stage.amount.toLocaleString()}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => {
                                      const currentDueIndex =
                                        program.stages.findIndex(
                                          (stage) => stage.status === "due"
                                        );
                                      const currentDueStage =
                                        program.stages[currentDueIndex];
                                      const totalPendingAmount =
                                        program.details.find(
                                          (d) => d.label === "Pending Payment"
                                        )?.value || 0;

                                      // Calculate new pending amount after payment
                                      const newPendingAmount =
                                        totalPendingAmount -
                                        (currentDueStage?.amount || 0);
                                      navigate("/sap/checkout", {
                                        state: {
                                          id: program.package_id,
                                          package_name:
                                            program.programName ||
                                            "Default Package",
                                          currency: program.currency,
                                          original_price:
                                            program.details.find(
                                              (d) => d.label === "Total Payment"
                                            )?.value || 0,
                                          installments: program.stages.map(
                                            (stage, i) => ({
                                              [`installment_${i + 1}_amount`]:
                                                stage.amount,
                                              active: i === currentDueIndex,
                                            })
                                          ),
                                          sub_service_id:
                                            program?.sub_service_id,
                                          scholars_program_id:
                                            program?.scholars_program_id,
                                          is_new_payment: false,
                                          installment_number:
                                            currentDueIndex + 1,
                                          pending_amount: newPendingAmount,
                                        },
                                      });
                                    }}
                                    className="bg-[#5B1A8E] hover:bg-[#481070] text-white text-[11px] font-semibold px-3 py-1.5 rounded-md transition-all"
                                  >
                                    Pay Now
                                  </button>
                                </div>
                              </>
                            )}

                            {/* Upcoming */}
                            {stage.status === "upcoming" && (
                              <>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="bg-[#F3F4F6] text-[#4B5563] px-2 py-[2px] rounded-md text-[11px] font-semibold">
                                    Upcoming
                                  </span>
                                </div>
                                <div className="mt-2 border-t border-dashed border-[#E5E7EB] pt-2">
                                  <p className="text-gray-600 font-medium">
                                    Amount
                                  </p>
                                  <p className="font-semibold text-gray-900 text-[15px] sm:text-[16px]">
                                    INR {stage.amount.toLocaleString()}
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
